import { Avatar, Button, IconButton, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { SearchUser } from 'lib-search-types';
import { MetaConversation } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import { Component } from 'react';

import { buttonize } from 'components/shared/util/buttonize';
import { getUserDisplayName } from 'utils/user';

import { numberFormatterInternational } from '../../../../app/utils/telephonyHelpers';

import styles from './userItem.module.scss';

type PropTypes = {
  user: SearchUser;
  metaConversation: MetaConversation;
  numberTransferRequests: Array<any>;
  transferToAgentRequests: Array<any>;
  disabled: string;
  disabledReason?: string;
  asyncStatus: string;
  highlighted: boolean;
  profile: User;
  className?: string;
  onAssistedTransferClick: (payload: { requestToUser: SearchUser }) => void;
  onBlindTransferClick: (payload: { requestToUser: SearchUser }) => void;
};

type State = {
  isOpen: boolean;
};

export default class UserItem extends Component<PropTypes, State> {
  constructor(props: PropTypes) {
    super(props);
    this.state = {
      isOpen: false,
    };
  }

  toggle = () => {
    this.setState({ isOpen: !this.state.isOpen });
  };

  render() {
    const {
      className,
      user,
      profile,
      disabled,
      disabledReason,
      metaConversation,
      highlighted,
      numberTransferRequests,
      transferToAgentRequests,
      asyncStatus,
      onAssistedTransferClick,
      onBlindTransferClick,
    } = this.props;

    const { isOpen } = this.state;

    let name = getUserDisplayName(user);
    let isNumberShown = false;
    let toPhoneNumberFormat = '';
    let disabledRender = disabled;
    let reassignDescription =
      disabledReason ??
      'Transfer conversation #' + metaConversation.conversation.id;

    if (user.phone_number !== null) {
      toPhoneNumberFormat = numberFormatterInternational({
        phoneNumber: user.phone_number as any,
      });
    }

    if (disabledReason === undefined && disabledRender === '') {
      if (metaConversation.requester?.id === user.id) {
        disabledRender = 'disabled';
        reassignDescription = 'This user is already in the conversation.';
      }

      if (disabledRender === '' && user.id === profile.id) {
        disabledRender = 'disabled';
        reassignDescription = 'You cannot transfer to yourself.';
      }

      const requestToAgent = transferToAgentRequests.find(
        (request) =>
          request.metaConversation.conversation.id ===
          metaConversation.conversation.id,
      );

      const requestToNumber = numberTransferRequests.find(
        (request) =>
          request.metaConversation.conversation.id ===
          metaConversation.conversation.id,
      );

      if (requestToAgent !== undefined || requestToNumber !== undefined) {
        disabledRender = 'disabled';
        reassignDescription = 'Request already sent waiting for response.';
      }
    } else if (disabledReason === undefined && asyncStatus === 'loading') {
      reassignDescription = 'Loading';
      disabledRender = 'disabled';
    }

    if (typeof user.name === 'string') {
      name = user.name;
    } else if (typeof user.phone_number === 'string') {
      name = user.phone_number;
      isNumberShown = true;
    } else if (typeof user.email === 'string') {
      name = user.email;
    }

    const rootClass = classnames(
      {
        [styles.root]: true,
        [styles.rootHighlighted]: highlighted,
        [styles.rootDim]: disabledRender === 'disabled',
      },
      className,
    );

    return (
      <div className={rootClass} {...buttonize(this.toggle)}>
        <div className={styles.header}>
          <div className={styles.avatar}>
            {user.id === 'newUser' ? (
              <Avatar icon="phone" />
            ) : (
              <Avatar user={user} />
            )}
          </div>
          <div className={styles.labels}>
            <div>{name}</div>
            <div>
              {!isNumberShown && user.phone_number !== null
                ? toPhoneNumberFormat
                : 'Contact'}
            </div>
          </div>
          <IconButton
            icon={isOpen ? 'arrow-down' : 'arrow-right'}
            className={styles.toggle}
            variant="tertiary"
            onClick={this.toggle}
          />
        </div>
        {isOpen && (
          <div className={styles.buttons}>
            <Tooltip content={reassignDescription}>
              <Button
                variant="secondary"
                type="button"
                style={{ marginRight: '8px' }}
                disabled={disabledRender === 'disabled'}
                onClick={
                  disabledRender === 'disabled'
                    ? () => {
                        /** Noop. */
                      }
                    : () => {
                        onBlindTransferClick({ requestToUser: user });
                      }
                }
                className={styles.btnTransferNow}
              >
                Transfer now
              </Button>
            </Tooltip>
            <Tooltip content={reassignDescription}>
              <Button
                variant="secondary"
                disabled={disabledRender === 'disabled'}
                type="button"
                onClick={
                  disabledRender === 'disabled'
                    ? () => {
                        /** Noop. */
                      }
                    : () => {
                        onAssistedTransferClick({ requestToUser: user });
                      }
                }
                className={styles.btnTalkToFirst}
              >
                Talk to {name} first
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
    );
  }
}
